import {FormEvent, useEffect, useState} from "react";
import {connect, ConnectedProps} from "react-redux";
import CodUserAPI from "../../api/CodUserAPI";
import {Button, Card, Col, Form, Row, Image} from "react-bootstrap";
import InputElement from "../../components/InputElement/InputElement";
import * as Input from "../../inputs";
import * as Inputs from '../../inputs';
import * as Validators from '../../util/validators';
import Forms from "../../util/Forms";
import {useParams} from "react-router-dom";
import AccountActions from "../../store/actions/AccountActions";
import {ContactMethodType, IconWithTextRecord, NewRegUserDTO, PreferredContactMethodDTO} from '../../types';
import {GeneralActions} from "../../store/actions/GeneralActions";
import styles from './CreateUserAccount.module.scss';
import VerificationCodeModal from "../../components/VerificationCodeModal/VerificationCodeModal";
import * as filters from '../../util/filters';
import MultiColumnInputElementForm from "../../components/MultiColumnInputElementForm/MultiColumnInputElementForm";
import CognitoAPI from "../../api/CognitoAPI";
import IconWithTextTable from "../IconWithTextTable/IconWithTextTable";
import PasswordValidation from "../../util/PasswordValidation";
import UserAccessPage from "../../hoc/UserAccessPage/UserAccessPage";

const map_state_to_props = null;

const map_dispatch_to_props = (dispatch: any) => ({
    actions: {
        show_spinning_wheel: () => dispatch(GeneralActions.show_spinning_wheel()),
        hide_spinning_wheel: () => dispatch(GeneralActions.hide_spinning_wheel()),
        show_error: (text: string) => dispatch(GeneralActions.show_error(text)),
        clear_error: () => dispatch(GeneralActions.clear_error()),
        go_home: () => dispatch(AccountActions.go_home()),
        set_error_from_response: (e: any) => dispatch(GeneralActions.set_error_from_response(e)),
    }
});

const connector = connect(map_state_to_props, map_dispatch_to_props);

interface CreateUserAccountForm extends Record<string, Inputs.InputField> {
    firstname: Inputs.TextField;
    lastname: Inputs.TextField;
    email: Inputs.EmailField;
    phone: Inputs.PhoneNumberField;
    password: Inputs.PasswordField;
    confirm_password: Inputs.PasswordField;
    preferred_contact_method: Inputs.SingleSelectField<PreferredContactMethodDTO>;
}

interface VerificationCodeForm extends Record<string, Inputs.InputField> {
    verification_code: Inputs.TextField;
    email: Inputs.ReadOnlyText;
}

const CreateUserAccount = ({actions}: ConnectedProps<typeof connector>) => {

    const params = useParams<{ account_guid?: string; '*'?: string }>();
    const { account_guid, "*": email } = params;
    
    const [create_user_account_form, set_create_user_account_form] = useState<CreateUserAccountForm>({
        firstname: {
            type: Inputs.FieldDataType.Text,
            label: 'First Name',
            touched: false,
            valid: false,
            dom_id: 'create-user-account-first-name',
            name: 'firstname',
            value: '',
            hide_valid: true,
            placeholder: 'First Name',
            floating_label: true,
            validator: Validators.required_text
        },
        lastname: {
            type: Inputs.FieldDataType.Text,
            label: 'Last Name',
            touched: false,
            valid: false,
            dom_id: 'create-user-account-last-name',
            name: 'lastname',
            value: '',
            hide_valid: true,
            placeholder: 'Last Name',
            floating_label: true,
            validator: Validators.required_text
        },
        email: {
            type: Inputs.FieldDataType.Email,
            label: 'Email',
            touched: false,
            valid: false,
            dom_id: 'create-user-account-email',
            name: 'email',
            value: '',
            hide_valid: true,
            placeholder: 'Email',
            floating_label: true,
            validator: Validators.required_text
        },
        phone: {
            type: Inputs.FieldDataType.PhoneNumber,
            label: 'Phone',
            touched: false,
            valid: false,
            hide_valid: true,
            dom_id: 'create-user-account-phone',
            name: 'phone',
            value: '',
            floating_label: true,
            filter: filters.max_length(10),
            validator: (input, _) => input.length === 10
        },
        password: {
            type: Inputs.FieldDataType.Password,
            label: 'Password',
            touched: false,
            valid: false,
            dom_id: 'create-user-account-pass',
            name: 'password:',
            value: '',
            hide_valid: true,
            floating_label: true,
            validator: Validators.combine_and([Validators.required_text, 
                Validators.does_not_contain_spaces,
                Validators.min_length(8),
                Validators.max_length(20),
                Validators.has_number,
                Validators.has_upper_case_letter,
                Validators.has_lower_case_letter,
                Validators.has_special_character,
                Validators.has_no_more_than_three_repetitive_characters,
                Validators.has_no_more_than_three_sequential_characters
            ])
        }, 
        confirm_password: {
            type: Inputs.FieldDataType.Password,
            touched: false,
            valid: false,
            dom_id: 'create-user-account-confirm-password',
            value: '',
            hide_valid: true,
            label: 'Confirm Password',
            floating_label: true,
            validator: (input, current_form) => (
                input === (current_form as CreateUserAccountForm).password.value
            )
        },
        preferred_contact_method: {
            type: Input.FieldDataType.SingleSelect,
            label: 'Preferred Contact Method',
            touched: false,
            valid: false,
            dom_id: 'create-user-account-preferred-contact-method',
            name: 'preferred-contact-method',
            value: {id: '-1', name: '', order: -1},
            hide_valid: true,
            options: [],
            floating_label: true,
            option_to_display_string: option => option.name,
            option_to_value_string: option => `${option.id}`,
            value_from_option_string: (value, options) => options.filter(option => option.id === value)[0]
        },
    });

    const [create_user_account_form_valid, set_create_user_account_form_valid] = useState<boolean>(false);
    const [tou_accepted, set_tou_accepted] = useState<boolean>(false);
    const [show_validation_modal, set_show_validation_modal] = useState<boolean>(false);
    const [verification_code_form, set_verification_code_form] = useState<VerificationCodeForm>({
        email: {
            type: Inputs.FieldDataType.ReadOnlyText,
            label: 'Email Address:',
            touched: false,
            valid: false,
            dom_id: 'verification-email',
            name: 'email',
            value: '',
            hide_valid: true,
        },
        verification_code: {
            type: Inputs.FieldDataType.Text,
            label: 'Verification Code',
            touched: false,
            valid: false,
            dom_id: 'verification-code-name',
            name: 'verification_code',
            value: '',
            hide_valid: true,
            placeholder: 'Verification Code',
            floating_label: true,
            validator: Validators.required_text
        }
    });
    const [verification_code_form_valid, set_verification_code_form_valid] = useState<boolean>(false);
    const [validation_list, set_validation_list] = useState<IconWithTextRecord[]>([]);

    const load_data = async () => {
        const [
            preferred_contact_methods,
        ] = await Promise.all([
            CodUserAPI.get_preferred_contact_methods()
        ]);

        set_create_user_account_form({
            ...create_user_account_form,
            preferred_contact_method: {
                ...create_user_account_form.preferred_contact_method,
                touched: true,
                valid: true,
                options: [...preferred_contact_methods].sort((a, b) => a.order - b.order),
                value: preferred_contact_methods[0]
            },
            email: {
                ...create_user_account_form.email,
                value: email ? email : '',
                valid: email ? true : false,
                touched: true,
                disabled: email ? true : false
            }
        })
        const list = PasswordValidation.get_initial_password_validation_list();
        set_validation_list(list);
    }

    useEffect(() => {
        load_data();
    }, [])

    const create_user_account_form_changed = (key: string, value: any) => {
        const {form, form_valid} = Forms.update_form({key, value, form: create_user_account_form});
        set_create_user_account_form_valid(form_valid);

        const list = PasswordValidation.get_password_validation_list(form.password.value);
        set_validation_list(list);

        const updated_error_form: CreateUserAccountForm = {
            ...(form as CreateUserAccountForm),
            password: {
                ...(form as CreateUserAccountForm).password,
                error: undefined
            }
        };

        if (form.confirm_password.touched && !form.confirm_password.valid) {
            const updated_confirm: Inputs.PasswordField = {
                ...updated_error_form.confirm_password,
                error: 'Re-enter your new password: '
            };
            updated_error_form.confirm_password = updated_confirm;
        }
        if (form.confirm_password.valid) {
            const updated_confirm: Inputs.PasswordField = {
                ...updated_error_form.confirm_password,
                error: undefined
            };
            updated_error_form.confirm_password = updated_confirm;
        }
        set_create_user_account_form(updated_error_form);
    };

    const create_user_account = async ({firstname, lastname, email, confirm_password, phone, preferred_contact_method}: 
        { firstname: string, lastname: string, email: string, confirm_password: string, phone: string, preferred_contact_method: string }) => {
        actions.show_spinning_wheel();
        const new_account_model: NewRegUserDTO = {
            user: {
                account_guid: account_guid!,
                first_name: firstname.trim(),
                last_name: lastname.trim(),
                preferred_contact_method: preferred_contact_method as ContactMethodType,
                phone_number: phone.trim(),
                email_address: email.trim(),
            }
        };

        try{
            set_verification_code_form({
                ...verification_code_form,
                email: {
                    ...verification_code_form.email,
                    touched: true,
                    valid: true,
                    value: new_account_model.user.email_address.trim()
                }
            });
            await CodUserAPI.createUser(new_account_model, confirm_password);
            set_show_validation_modal(true);
        } catch (e: any) {
            actions.set_error_from_response(e);
        }
        finally {
            actions.hide_spinning_wheel();
        }
    }

    const account_form_submit = (event: FormEvent) => {
        event.preventDefault();
        if (!tou_accepted) return;
        const {form, form_valid} = Forms.filter_and_validate_form(create_user_account_form);
        const final_form = form as CreateUserAccountForm;

        if (!form_valid) {
            set_create_user_account_form(final_form);
            set_create_user_account_form_valid(form_valid);
            return;
        }
        create_user_account({
            firstname: final_form.firstname.value,
            lastname: final_form.lastname.value,
            email: final_form.email.value,
            confirm_password: final_form.confirm_password.value,
            phone: final_form.phone.value,
            preferred_contact_method: final_form.preferred_contact_method.value.name
        });

    };

    const on_verification_code_submit = async (event: FormEvent) => {
        event.preventDefault();
        const {form, form_valid} = Forms.filter_and_validate_form(verification_code_form);
        const final_form = form as VerificationCodeForm;

        if (!form_valid) {
            set_verification_code_form(final_form);
            return;
        }
        try {
            actions.show_spinning_wheel();
            await CognitoAPI.submitVerificationCode(final_form.email.value.trim(), final_form.verification_code.value.trim());
            actions.hide_spinning_wheel();
            set_show_validation_modal(false);
            actions.go_home();     
        } catch (error: any) {
            actions.show_error(error.message);
            actions.hide_spinning_wheel();
        }
        
    };

    const on_verification_code_change  = (key: string, value: any) => {
        const {form, form_valid} = Forms.update_form({key, value, form: verification_code_form});
        set_verification_code_form(form as VerificationCodeForm);
        set_verification_code_form_valid(form_valid);
    };


    const on_verification_code_resend = async () => {
        try{
            await CognitoAPI.resendConfirmationCode(verification_code_form.email.value.trim());
        } catch (error: any) {
            actions.show_error(error.message);
        }    
    };

    const render_tou_checkbox = () => (
        <Row>
            <Col className={'d-flex justify-content-center'}>
                <InputElement
                    element={{
                        type: Inputs.FieldDataType.Checkbox,
                        touched: true,
                        valid: true,
                        dom_id: 'account-creation-accept',
                        label_node: (
                            <span className={styles.SupStyle}>
                                <strong>
                                   I Accept the <a href="/tou" target="_blank" rel="noopener noreferrer">Terms & Conditions</a> and <a href="/pp" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                                </strong>
                            </span>
                        ),
                        value: tou_accepted
                    } as Input.CheckboxField}
                    group_className={'mb-3'}
                    changed={(new_value: boolean) => set_tou_accepted(new_value)}/>
            </Col>
        </Row>
    );

    const render_bottom_row = () => (
        <>
            <Row>
                <Col className={'d-flex justify-content-center mb-3'}>
                    <Button
                        type={'submit'}
                        variant={'teal'}
                        className={'px-4 rounded-pill'}
                        disabled={!create_user_account_form_valid || !tou_accepted}
                    >Submit</Button>
                </Col>
            </Row>
            <Row className={'mb-1 mt-2'}>
                <Col className={'text-center'}>
                    <sup>
                        Do not use "Remember me" on public or shared computers.
                    </sup>
                </Col>
            </Row>
        </>
    );


    return (
        <UserAccessPage>
            <Col xs={12} className={'mb-3 mb-md-0'}>
                <Card.Title>
                    Profile Information
                </Card.Title>
                <Form onSubmit={account_form_submit}>
                    <MultiColumnInputElementForm
                        changed={create_user_account_form_changed}
                        fields={create_user_account_form}
                        group_className={`mb-3`}
                        included_keys={['firstname', 'lastname', 'email', 'phone', ]}
                        columns={2}/>
                    <MultiColumnInputElementForm
                        changed={create_user_account_form_changed}
                        fields={create_user_account_form}
                        group_className={`mb-3`}
                        included_keys={['preferred_contact_method']}
                        columns={2}/>
                    <MultiColumnInputElementForm
                        changed={create_user_account_form_changed}
                        fields={create_user_account_form}
                        group_className={`mb-3`}
                        included_keys={['password', 'confirm_password']}
                        columns={2}/>
                    <IconWithTextTable  records={validation_list} /> 
                    {render_tou_checkbox()}
                    {render_bottom_row()}
                </Form>
            </Col>
            <VerificationCodeModal
                        show={show_validation_modal}
                        on_hide={() => set_show_validation_modal(false)}
                        size={'lg'}
                        form={verification_code_form}
                        formValid={verification_code_form_valid}
                        onSubmit={on_verification_code_submit}
                        onFormChange={on_verification_code_change}
                        onVerificationCodeResend={on_verification_code_resend}
                    />
            
        </UserAccessPage>
    );
}

export default connector(CreateUserAccount);